import { Injectable } from '@angular/core';
import { NavigationExtras } from '@angular/router';
import { ROUTES } from '@app/constants/routes.constants';
import { type IEBNotification } from '@shared/models/entities/notification.interface';
import { NotificationEventType } from '@shared/models/notification-enums';

type NotificationNavigationPlatform = 'desktop' | 'mobile';

export type NotificationTargetKind =
  | 'task'
  | 'thread'
  | 'campaign'
  | 'campaignCommunication';

export type NotificationTargetUnavailableReason =
  | 'missing-notification'
  | 'missing-entity-id'
  | 'unsupported-entity-type';

export interface NotificationTargetContext {
  platform?: NotificationNavigationPlatform;
}

export interface NotificationTarget {
  type: 'available';
  kind: NotificationTargetKind;
  commands: unknown[];
  navigationExtras: NavigationExtras;
  labelKey: string;
  descriptionKey: string;
  icon: string;
  metadata: Record<string, unknown>;
}

export interface NotificationTargetUnavailable {
  type: 'unavailable';
  reason: NotificationTargetUnavailableReason;
  messageKey: string;
}

export type NotificationTargetResolution = NotificationTarget | NotificationTargetUnavailable;

const enum EntityType {
  TASK = 'TASK',
  THREAD = 'THREAD',
  CAMPAIGN = 'CAMPAIGN',
}

@Injectable({ providedIn: 'root' })
export class NotificationTargetResolverService {
  resolve(
    notification: IEBNotification | undefined | null,
    context: NotificationTargetContext = {},
  ): NotificationTargetResolution {
    if (!notification) {
      return this.unavailable('missing-notification', 'notification.navigation.unavailable');
    }

    const entityId = this.trimToNull(notification.entityId);
    if (!entityId) {
      return this.unavailable('missing-entity-id', 'notification.navigation.unavailable');
    }

    const platform = context.platform ?? 'desktop';
    const entityType = this.trimToNull(notification.entityType)?.toUpperCase() ?? null;
    const metadata = this.parseMetadata(notification.metadata);

    switch (entityType) {
      case EntityType.TASK:
        return this.createTarget(notification, metadata, {
          kind: 'task',
          commands: [ROUTES.TASKS_DO, entityId],
          labelKey: 'notification.navigation.openTask',
          descriptionKey: 'notification.navigation.taskTarget',
          icon: 'checkbox-outline',
        });

      case EntityType.THREAD:
        return this.createThreadTarget(notification, metadata, entityId, platform);

      case EntityType.CAMPAIGN:
        return this.createCampaignTarget(notification, metadata, entityId, platform);

      default:
        return this.unavailable(
          'unsupported-entity-type',
          'notification.navigation.unknownTarget',
        );
    }
  }

  private createThreadTarget(
    notification: IEBNotification,
    metadata: Record<string, unknown>,
    threadId: string,
    platform: NotificationNavigationPlatform,
  ): NotificationTarget {
    const navigationExtras = this.withNotificationState(notification, metadata);

    if (platform === 'desktop') {
      return {
        type: 'available',
        kind: 'thread',
        commands: [ROUTES.DISCUSSIONS],
        navigationExtras: {
          ...navigationExtras,
          queryParams: { threadId },
        },
        labelKey: 'notification.navigation.openDiscussion',
        descriptionKey: 'notification.navigation.discussionTarget',
        icon: 'chatbubbles-outline',
        metadata,
      };
    }

    return this.createTarget(notification, metadata, {
      kind: 'thread',
      commands: [ROUTES.DISCUSSIONS, threadId],
      labelKey: 'notification.navigation.openDiscussion',
      descriptionKey: 'notification.navigation.discussionTarget',
      icon: 'chatbubbles-outline',
    });
  }

  private createCampaignTarget(
    notification: IEBNotification,
    metadata: Record<string, unknown>,
    campaignId: string,
    platform: NotificationNavigationPlatform,
  ): NotificationTarget {
    const isCommunication =
      this.normalizeType(notification.type) === NotificationEventType.CAMPAIGN_COMMUNICATION;

    if (isCommunication && platform === 'desktop') {
      return this.createTarget(notification, metadata, {
        kind: 'campaignCommunication',
        commands: [ROUTES.CAMPAIGN_MGMT],
        labelKey: 'notification.navigation.openCampaignMessage',
        descriptionKey: 'notification.navigation.campaignMessageTarget',
        icon: 'megaphone-outline',
      });
    }

    return this.createTarget(notification, metadata, {
      kind: isCommunication ? 'campaignCommunication' : 'campaign',
      commands: [ROUTES.CAMPAIGN_DETAIL, campaignId],
      labelKey: isCommunication
        ? 'notification.navigation.openCampaignMessage'
        : 'notification.navigation.openCampaign',
      descriptionKey: isCommunication
        ? 'notification.navigation.campaignMessageTarget'
        : 'notification.navigation.campaignTarget',
      icon: isCommunication ? 'megaphone-outline' : 'flag-outline',
    });
  }

  private createTarget(
    notification: IEBNotification,
    metadata: Record<string, unknown>,
    target: Omit<NotificationTarget, 'type' | 'navigationExtras' | 'metadata'>,
  ): NotificationTarget {
    return {
      type: 'available',
      ...target,
      navigationExtras: this.withNotificationState(notification, metadata),
      metadata,
    };
  }

  private withNotificationState(
    notification: IEBNotification,
    metadata: Record<string, unknown>,
  ): NavigationExtras {
    const state: Record<string, unknown> = {
      fromNotification: true,
      notificationId: notification.notificationId,
      notificationType: notification.type,
    };

    const highlightId = this.getMetadataString(metadata, [
      'commentId',
      'campaignCommunicationId',
      'highlightId',
    ]);
    if (highlightId) {
      state['highlightId'] = highlightId;
    }

    return { state };
  }

  private parseMetadata(metadata: string | null): Record<string, unknown> {
    if (!metadata) {
      return {};
    }

    try {
      const parsed: unknown = JSON.parse(metadata);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }

    return {};
  }

  private getMetadataString(metadata: Record<string, unknown>, keys: string[]): string | null {
    for (const key of keys) {
      const value = metadata[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    return null;
  }

  private unavailable(
    reason: NotificationTargetUnavailableReason,
    messageKey: string,
  ): NotificationTargetUnavailable {
    return { type: 'unavailable', reason, messageKey };
  }

  private trimToNull(value: string | null | undefined): string | null {
    const trimmed = String(value ?? '').trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private normalizeType(type: string | null | undefined): string {
    return String(type ?? '').trim().toLowerCase();
  }
}